
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Index from "@/pages/Index";
import { AuthScreen } from "@/components/auth/AuthScreen";
import { Toaster } from "@/components/ui/toaster";
import { LandingPage } from "@/components/landing/LandingPage";
import Support from "@/pages/Support";
import FAQ from "@/pages/FAQ";
import { MainLayout } from "@/components/layout/MainLayout";
import Pricing from "@/pages/Pricing";
import PaymentCallback from "@/pages/PaymentCallback";
import { NotFoundPage } from "@/components/layout/NotFoundPage";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    // Check initial auth state
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
      
      // Handle explicit logout
      if (event === 'SIGNED_OUT') {
        localStorage.setItem('explicitly_logged_out', 'true');
      } else if (event === 'SIGNED_IN') {
        localStorage.removeItem('explicitly_logged_out');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Show nothing while we're checking auth state
  if (isAuthenticated === null) {
    return null;
  }

  return (
    <Router>
      <MainLayout>
        <Routes>
          {/* Root route - redirect based on auth status */}
          <Route 
            path="/" 
            element={
              isAuthenticated && !localStorage.getItem('explicitly_logged_out') ? (
                <Navigate to="/workspace" replace />
              ) : (
                <Navigate to="/landing" replace />
              )
            } 
          />

          {/* Landing page - always accessible */}
          <Route 
            path="/landing" 
            element={<LandingPage />}
          />

          {/* Auth route - always accessible */}
          <Route 
            path="/auth" 
            element={<AuthScreen />}
          />

          {/* Protected workspace route */}
          <Route 
            path="/workspace" 
            element={
              isAuthenticated ? (
                <Index />
              ) : (
                <Navigate to="/auth" replace />
              )
            } 
          />

          {/* Public routes - accessible to all users regardless of auth status */}
          <Route path="/support" element={<Support />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/payment-callback" element={<PaymentCallback />} />
          
          {/* 404 route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </MainLayout>
      <Toaster />
    </Router>
  );
}

export default App;
